import { Icon } from "@/components/ui/Icon";
import { format, addMinutes } from "date-fns";
import { AddressInput } from "@/components/forms/AddressInput";
import { DatePicker } from "../DatePicker";
import { TimePicker } from "../TimePicker";
import { MAX_STOPS } from "../constants";
import type { Locale } from "@/config/i18n";
import type {
  ReservationFormData,
  AddressSuggestion,
  TripType,
} from "../types";
import type { ReservationUi } from "../reservationFormUi";

type TripDetailsSectionProps = {
  t: ReservationUi["step1"];
  locale: Locale;
  tripType: TripType;
  formData: ReservationFormData;
  setFormData: React.Dispatch<React.SetStateAction<ReservationFormData>>;
  // Autocomplétion d'adresses
  departureSuggestions: AddressSuggestion[];
  arrivalSuggestions: AddressSuggestion[];
  stopSuggestions: Record<string, AddressSuggestion[]>;
  showDepartureSuggestions: boolean;
  showArrivalSuggestions: boolean;
  showStopSuggestions: Record<string, boolean>;
  setShowDepartureSuggestions: (show: boolean) => void;
  setShowArrivalSuggestions: (show: boolean) => void;
  setShowStopSuggestions: React.Dispatch<
    React.SetStateAction<Record<string, boolean>>
  >;
  isLoadingDeparture: boolean;
  isLoadingArrival: boolean;
  isLoadingStop: Record<string, boolean>;
  handleAddressInput: (
    e: React.ChangeEvent<HTMLInputElement>,
    type: "departure" | "arrival" | "returnArrival" | "stop",
    stopId?: string,
  ) => void;
  selectSuggestion: (
    suggestion: AddressSuggestion,
    type: "departure" | "arrival" | "returnArrival" | "stop",
    stopId?: string,
  ) => void;
  filterByPOIType: (
    type: "gare" | "aeroport" | "destination" | "hotel",
    context: "departure" | "arrival" | "stop",
    stopId?: string,
  ) => void;
  // Étapes (multi-trajets)
  addStop: () => void;
  removeStop: (stopId: string) => void;
  updateStop: (stopId: string, address: string) => void;
  updateStopWaitingTime: (stopId: string, waitingTime: number) => void;
  customizeReturn: boolean;
};

/**
 * Section « Trajet » : adresses départ/arrivée, date/heure, champs de retour
 * (aller-retour) et étapes intermédiaires (multi-trajets). Bloc présentationnel
 * extrait verbatim de ReservationForm (aucun changement de comportement) — la
 * logique « now: » du DatePicker/TimePicker est conservée à l'identique.
 */
export const TripDetailsSection = ({
  t,
  locale,
  tripType,
  formData,
  setFormData,
  departureSuggestions,
  arrivalSuggestions,
  stopSuggestions,
  showDepartureSuggestions,
  showArrivalSuggestions,
  showStopSuggestions,
  setShowDepartureSuggestions,
  setShowArrivalSuggestions,
  setShowStopSuggestions,
  isLoadingDeparture,
  isLoadingArrival,
  isLoadingStop,
  handleAddressInput,
  selectSuggestion,
  filterByPOIType,
  addStop,
  removeStop,
  updateStop,
  updateStopWaitingTime,
  customizeReturn,
}: TripDetailsSectionProps) => {
  return (
    <section className="mb-8">
      <h2 className="mb-6 flex items-center gap-2 text-2xl font-semibold text-black">
        <Icon icon="ph:map-pin-line" className="h-6 w-6 text-gold-700" />
        <span>{t.tripDetailsHeading}</span>
      </h2>

      <div className="-mx-2 flex flex-wrap gap-y-4">
        <AddressInput
          id="departure"
          name="departure"
          value={formData.departure}
          onChange={(e) => handleAddressInput(e, "departure")}
          onSelect={(suggestion) => selectSuggestion(suggestion, "departure")}
          suggestions={departureSuggestions}
          showSuggestions={showDepartureSuggestions}
          setShowSuggestions={setShowDepartureSuggestions}
          isLoading={isLoadingDeparture}
          label={t.departureLabel}
          placeholder={t.departurePlaceholder}
          onFilterByType={(type) => filterByPOIType(type, "departure")}
          showQuickFilters={true}
          locale={locale}
        />

        {tripType === "disposition" ? (
          <div className="w-full px-2 md:w-1/2">
            <label className="mb-2 block font-medium text-gold-700">
              {t.destination}
            </label>
            <div className="flex h-12 items-center gap-3 rounded-lg border border-gold-300 bg-gold-100 px-4">
              <Icon icon="ph:map-pin" className="h-5 w-5 text-gold-700" />
              <span className="text-gold-800">{t.anywhere}</span>
            </div>
          </div>
        ) : (
          tripType !== "multi-trip" && (
            <AddressInput
              id="arrival"
              name="arrival"
              value={formData.arrival}
              onChange={(e) => handleAddressInput(e, "arrival")}
              onSelect={(suggestion) => selectSuggestion(suggestion, "arrival")}
              suggestions={arrivalSuggestions}
              showSuggestions={showArrivalSuggestions}
              setShowSuggestions={setShowArrivalSuggestions}
              isLoading={isLoadingArrival}
              label={t.arrivalLabel}
              placeholder={t.arrivalPlaceholder}
              onFilterByType={(type) => filterByPOIType(type, "arrival")}
              showQuickFilters={true}
              locale={locale}
            />
          )
        )}
      </div>

      <div className="-mx-2 mt-4 flex flex-wrap gap-y-4">
        <DatePicker
          id="date"
          value={formData.date}
          onChange={(value) => {
            const today = format(new Date(), "yyyy-MM-dd");
            if (
              value === today &&
              formData.time &&
              !formData.time.startsWith("now:")
            ) {
              const [hour, minute] = formData.time.split(":");
              const selectedTime = new Date();
              selectedTime.setHours(parseInt(hour), parseInt(minute));

              if (selectedTime < addMinutes(new Date(), 30)) {
                const now = new Date();
                const roundedMinutes = Math.ceil(now.getMinutes() / 5) * 5;
                const adjustedTime = addMinutes(
                  now,
                  roundedMinutes - now.getMinutes() + 30,
                );
                setFormData((prev) => ({
                  ...prev,
                  date: value,
                  time: `now:${format(adjustedTime, "HH:mm")}`,
                }));
                return;
              }
            }
            setFormData((prev) => ({ ...prev, date: value }));
          }}
          label={t.dateLabel}
          locale={locale}
          isNowSelected={formData.time?.startsWith("now:")}
          onSelectNow={() => {
            const now = new Date();
            const roundedMinutes = Math.ceil(now.getMinutes() / 5) * 5;
            const adjustedTime = addMinutes(
              now,
              roundedMinutes - now.getMinutes() + 30,
            );
            setFormData((prev) => ({
              ...prev,
              date: format(now, "yyyy-MM-dd"),
              time: `now:${format(adjustedTime, "HH:mm")}`,
            }));
          }}
        />

        <TimePicker
          id="time"
          value={formData.time}
          onChange={(value) => {
            if (value.startsWith("now:")) {
              setFormData((prev) => ({
                ...prev,
                time: value,
                date: format(new Date(), "yyyy-MM-dd"),
              }));
            } else {
              setFormData((prev) => ({
                ...prev,
                time: value,
              }));
            }
          }}
          label={t.timeLabel}
          locale={locale}
          selectedDate={formData.date}
          onSelectNow={() => {
            setFormData((prev) => ({
              ...prev,
              date: format(new Date(), "yyyy-MM-dd"),
            }));
          }}
        />
      </div>

      {tripType === "round-trip" && (
        <>
          <div className="-mx-2 mt-4 flex flex-wrap gap-y-4">
            <DatePicker
              id="returnDate"
              value={formData.returnDate}
              onChange={(value) =>
                setFormData((prev) => ({ ...prev, returnDate: value }))
              }
              label={t.returnDateLabel}
              locale={locale}
              isNowSelected={false}
            />

            <TimePicker
              id="returnTime"
              value={formData.returnTime}
              onChange={(value) =>
                setFormData((prev) => ({ ...prev, returnTime: value }))
              }
              label={t.returnTimeLabel}
              locale={locale}
              selectedDate={formData.returnDate}
            />
          </div>

          {customizeReturn && (
            <div className="-mx-2 mt-4">
              <AddressInput
                id="returnArrival"
                name="returnArrival"
                value={formData.returnArrival}
                onChange={(e) => handleAddressInput(e, "returnArrival")}
                onSelect={(suggestion) =>
                  selectSuggestion(suggestion, "returnArrival")
                }
                suggestions={arrivalSuggestions}
                showSuggestions={showArrivalSuggestions}
                setShowSuggestions={setShowArrivalSuggestions}
                isLoading={isLoadingArrival}
                label={t.returnAddressLabel}
                placeholder={t.returnAddressPlaceholder}
                onFilterByType={(type) => filterByPOIType(type, "arrival")}
                showQuickFilters={true}
                locale={locale}
              />
            </div>
          )}
        </>
      )}

      {tripType === "multi-trip" && (
        <>
          <div className="mt-4 space-y-4">
            {formData.stops.map((stop, index) => (
              <div key={stop.id} className="flex flex-wrap items-start gap-4">
                <div className="flex-1">
                  <AddressInput
                    id={`stop-${stop.id}`}
                    name={`stop-${stop.id}`}
                    value={stop.address}
                    onChange={(e) => {
                      handleAddressInput(e, "stop", stop.id);
                      updateStop(stop.id, e.target.value);
                    }}
                    onSelect={(suggestion) =>
                      selectSuggestion(suggestion, "stop", stop.id)
                    }
                    suggestions={stopSuggestions[stop.id] || []}
                    showSuggestions={showStopSuggestions[stop.id] || false}
                    setShowSuggestions={(show) =>
                      setShowStopSuggestions((prev) => ({
                        ...prev,
                        [stop.id]: show,
                      }))
                    }
                    isLoading={isLoadingStop[stop.id] || false}
                    label={t.stepLabel(index + 1)}
                    placeholder={t.stopPlaceholder}
                    onFilterByType={(type) => filterByPOIType(type, "stop")}
                    showQuickFilters={true}
                    locale={locale}
                  />
                </div>
                <div className="flex items-end gap-2">
                  <div className="w-32">
                    <label className="mb-2 block text-sm font-medium text-gold-700">
                      {t.waitingTime}
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        title="Temps d'attente"
                        placeholder="0"
                        type="number"
                        min="0"
                        max="60"
                        value={stop.waitingTime}
                        onChange={(e) =>
                          updateStopWaitingTime(
                            stop.id,
                            parseInt(e.target.value) || 0,
                          )
                        }
                        className="h-12 w-20 rounded-lg border border-gold-300 px-3 text-gold-800"
                      />
                      <span className="text-sm text-gold-700/80">{t.min}</span>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeStop(stop.id)}
                    className="flex h-12 w-12 items-center justify-center rounded-lg border border-gold-300 text-gold-700 transition-colors hover:bg-gold-100"
                    title={t.removeStop}
                  >
                    <Icon
                      icon="ph:trash"
                      className="h-5 min-h-5 w-5 min-w-5 text-red-500"
                    />
                  </button>
                </div>
              </div>
            ))}

            {formData.stops.length < MAX_STOPS && (
              <button
                type="button"
                onClick={addStop}
                className="flex items-center gap-2 rounded-lg border border-gold-300 px-4 py-2 text-sm text-gold-700 transition-colors hover:bg-gold-100"
              >
                <Icon icon="ph:plus-circle" className="h-5 w-5" />
                <span>{t.addStop(formData.stops.length, MAX_STOPS)}</span>
              </button>
            )}
          </div>

          <div className="mt-4">
            <AddressInput
              id="arrival"
              name="arrival"
              value={formData.arrival}
              onChange={(e) => handleAddressInput(e, "arrival")}
              onSelect={(suggestion) => selectSuggestion(suggestion, "arrival")}
              suggestions={arrivalSuggestions}
              showSuggestions={showArrivalSuggestions}
              setShowSuggestions={setShowArrivalSuggestions}
              isLoading={isLoadingArrival}
              label={t.finalArrivalLabel}
              placeholder={t.finalArrivalPlaceholder}
              onFilterByType={(type) => filterByPOIType(type, "arrival")}
              showQuickFilters={true}
              locale={locale}
            />
          </div>
        </>
      )}
    </section>
  );
};
